import { Badge, Loader, Stack, Table, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { PortalMaterialOrderRO } from '@routellers/types/dist/partners-portal/portal-material-order.ro';
import { PortalPartnerRO } from '@routellers/types/dist/partners-portal/portal-partner.ro';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api, apiErrorMessage } from '../api';
import { EntityThumb } from '../components/entity-thumb';
import { ListPagination } from '../components/list-pagination';
import { PageHeader } from '../components/page-header';
import { MaterialOrderEditor } from '../editors/material-order-editor';
import { slicePage } from '../utils/list';
import { formatDate, orderStatusColors, orderStatusLabel } from '../utils/portal';

type OrderRow = PortalMaterialOrderRO & { partner?: PortalPartnerRO };

export const OrdersPage = () => {
    const { t } = useTranslation();
    const [orders, setOrders] = useState<OrderRow[]>([]);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [editorOrder, setEditorOrder] = useState<OrderRow | null>(null);

    const load = useCallback(async () => {
        setLoading(true);

        try {
            const { data } = await api.partnersPortalAdminApi.materialOrders(undefined);
            setOrders(data || []);
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
            setOrders([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const pageRows = slicePage(orders, page);

    return (
        <Stack gap='md' style={{ flex: 1, minHeight: 0 }}>
            <PageHeader title={t('pages.orders.title')} />

            {loading ? (
                <Loader />
            ) : (
                <>
                    <Table.ScrollContainer minWidth={800}>
                        <Table striped highlightOnHover withTableBorder>
                            <Table.Thead>
                                <Table.Tr>
                                    <Table.Th>{t('pages.orders.columns.date')}</Table.Th>
                                    <Table.Th>{t('pages.orders.columns.venue')}</Table.Th>
                                    <Table.Th ta='right'>{t('pages.orders.columns.qty')}</Table.Th>
                                    <Table.Th>{t('pages.orders.columns.status')}</Table.Th>
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                                {pageRows.map((row) => {
                                    const venueName = row.partner
                                        ? row.partner.companyName || row.partner.name
                                        : `Partner #${row.partnerId}`;

                                    return (
                                        <Table.Tr
                                            key={row.id}
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => setEditorOrder(row)}
                                        >
                                            <Table.Td>{formatDate(row.createdAt)}</Table.Td>
                                            <Table.Td>
                                                <EntityThumb
                                                    src={row.partner?.avatarUrl}
                                                    name={venueName}
                                                    subtitle={row.partner?.email}
                                                    radius='md'
                                                />
                                            </Table.Td>
                                            <Table.Td ta='right'>{row.quantity}</Table.Td>
                                            <Table.Td>
                                                <Badge
                                                    color={orderStatusColors[row.status] || 'gray'}
                                                    variant='light'
                                                >
                                                    {orderStatusLabel(t, row.status)}
                                                </Badge>
                                            </Table.Td>
                                        </Table.Tr>
                                    );
                                })}
                                {!pageRows.length && (
                                    <Table.Tr>
                                        <Table.Td colSpan={4}>
                                            <Text c='dimmed' ta='center' py='md'>
                                                {t('pages.orders.empty')}
                                            </Text>
                                        </Table.Td>
                                    </Table.Tr>
                                )}
                            </Table.Tbody>
                        </Table>
                    </Table.ScrollContainer>
                    <ListPagination page={page} totalItems={orders.length} onChange={setPage} />
                </>
            )}

            <MaterialOrderEditor
                opened={editorOrder != null}
                order={editorOrder}
                onClose={() => setEditorOrder(null)}
                onSaved={load}
            />
        </Stack>
    );
};
